import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { PermissionService } from '@shared/services/permission.service';
import { PERMISSIONS } from '@shared/constants/permissions';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule, AppMenuitem, RouterModule],
  template: `<ul class="layout-menu">
    <ng-container *ngFor="let item of model; let i = index">
      <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
      <li *ngIf="item.separator" class="menu-separator"></li>
    </ng-container>
  </ul> `,
})
export class AppMenu {
  private permissionService = inject(PermissionService);
  model: MenuItem[] = [];

  private can(permission: string): boolean {
    return this.permissionService.hasPermission(permission);
  }

  ngOnInit() {
    this.model = [
      {
        label: '',
        items: [
          { label: 'Home', icon: 'pi pi-fw pi-home', routerLink: ['/'] },
          {
            label: 'Users',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/users'],
            visible: this.can(PERMISSIONS.USERS_READ),
          },
          {
            label: 'Vouchers',
            icon: 'pi pi-fw pi-tags',
            routerLink: ['/vouchers'],
            visible: this.can(PERMISSIONS.VOUCHERS_READ),
          },
          {
            label: 'Audit Logs',
            icon: 'pi pi-fw pi-history',
            routerLink: ['/audit-log'],
            visible: this.can(PERMISSIONS.AUDIT_LOGS_READ),
          },
          {
            label: 'Articles',
            icon: 'pi pi-fw pi-book',
            routerLink: ['/articles'],
            visible: this.can(PERMISSIONS.ARTICLES_READ_DASHBOARD),
          },
          {
            label: 'Notifications',
            icon: 'pi pi-fw pi-bell',
            visible:
              this.can(PERMISSIONS.NOTIFICATION_TEMPLATES_READ) ||
              this.can(PERMISSIONS.NOTIFICATIONS_READ) ||
              this.can(PERMISSIONS.NOTIFICATIONS_SEND) ||
              this.can(PERMISSIONS.NOTIFICATIONS_BROADCAST) ||
              this.can(PERMISSIONS.NOTIFICATION_TRIGGERS_READ),
            items: [
              {
                label: 'Templates',
                icon: 'pi pi-fw pi-file-edit',
                routerLink: ['/notifications/templates'],
                visible: this.can(PERMISSIONS.NOTIFICATION_TEMPLATES_READ),
              },
              {
                label: 'Send Notification',
                icon: 'pi pi-fw pi-send',
                routerLink: ['/notifications/send'],
                visible: this.can(PERMISSIONS.NOTIFICATIONS_SEND),
              },
              {
                label: 'Delivery History',
                icon: 'pi pi-fw pi-clock',
                routerLink: ['/notifications/history'],
                visible: this.can(PERMISSIONS.NOTIFICATIONS_READ),
              },
              {
                label: 'Email Campaigns',
                icon: 'pi pi-fw pi-envelope',
                routerLink: ['/notifications/broadcast/list'],
                visible: this.can(PERMISSIONS.NOTIFICATIONS_BROADCAST),
              },
              {
                label: 'Triggers',
                icon: 'pi pi-fw pi-bolt',
                routerLink: ['/notifications/triggers'],
                visible: this.can(PERMISSIONS.NOTIFICATION_TRIGGERS_READ),
              },
            ],
          },
          {
            label: 'Permissions',
            icon: 'pi pi-fw pi-key',
            routerLink: ['/permissions'],
            visible: this.can(PERMISSIONS.PERMISSIONS_READ),
          },
          {
            label: 'Subscriptions',
            icon: 'pi pi-fw pi-credit-card',
            visible: this.can(PERMISSIONS.SUBSCRIPTIONS_READ),
            items: [
              {
                label: 'Plans',
                icon: 'pi pi-fw pi-wallet',
                routerLink: ['/subscriptions'],
                visible: this.can(PERMISSIONS.SUBSCRIPTIONS_READ),
              },
              {
                label: 'Comparison Table',
                icon: 'pi pi-fw pi-table',
                routerLink: ['/subscriptions/comparison-table'],
                visible: this.can(PERMISSIONS.SUBSCRIPTIONS_READ),
              },
              {
                label: 'Comparison Features',
                icon: 'pi pi-fw pi-list',
                routerLink: ['/subscriptions/comparison-features'],
                visible: this.can(PERMISSIONS.SUBSCRIPTIONS_READ),
              },
            ],
          },
          {
            label: 'Reports',
            icon: 'pi pi-fw pi-chart-pie',
            visible:
              this.can(PERMISSIONS.REPORTS_OVERVIEW) ||
              this.can(PERMISSIONS.REPORTS_PERFORMANCE) ||
              this.can(PERMISSIONS.REPORTS_REVENUE) ||
              this.can(PERMISSIONS.REPORTS_SUBSCRIPTIONS) ||
              this.can(PERMISSIONS.REPORTS_PORTFOLIOS),
            items: [
              {
                label: 'Overview',
                icon: 'pi pi-fw pi-eye',
                routerLink: ['/reports/overview'],
                visible: this.can(PERMISSIONS.REPORTS_OVERVIEW),
              },
              {
                label: 'Performance',
                icon: 'pi pi-fw pi-chart-bar',
                routerLink: ['/reports/performance'],
                visible: this.can(PERMISSIONS.REPORTS_PERFORMANCE),
              },
              {
                label: 'Charts',
                icon: 'pi pi-fw pi-chart-line',
                routerLink: ['/reports/charts'],
                visible:
                  this.can(PERMISSIONS.REPORTS_REVENUE) ||
                  this.can(PERMISSIONS.REPORTS_SUBSCRIPTIONS),
              },
              {
                label: 'UTM Sources',
                icon: 'pi pi-fw pi-link',
                routerLink: ['/reports/utm-sources'],
                visible: this.can(PERMISSIONS.REPORTS_PERFORMANCE),
              },
              {
                label: 'Subscribers',
                icon: 'pi pi-fw pi-id-card',
                routerLink: ['/reports/subscribers'],
                visible: this.can(PERMISSIONS.REPORTS_PERFORMANCE),
              },
              {
                label: 'Portfolios',
                icon: 'pi pi-fw pi-briefcase',
                routerLink: ['/reports/portfolios'],
                visible: this.can(PERMISSIONS.REPORTS_PORTFOLIOS),
              },
            ],
          },
        ],
      },
      {
        label: 'Market Management',
        icon: 'pi pi-fw pi-briefcase',
        items: [
          {
            label: 'Sectors',
            icon: 'pi pi-fw pi-sitemap',
            routerLink: ['/sectors'],
            visible: this.can(PERMISSIONS.SECTORS_READ),
          },
          {
            label: 'Stocks',
            icon: 'pi pi-fw pi-chart-line',
            routerLink: ['/stocks'],
            visible: this.can(PERMISSIONS.STOCK_READ),
          },
          {
            label: 'News',
            icon: 'pi pi-fw pi-file-o',
            routerLink: ['/stocks/news'],
            visible: this.can(PERMISSIONS.NEWS_READ),
          },
          {
            label: 'IPOs',
            icon: 'pi pi-fw pi-dollar',
            routerLink: ['/stocks/ipos'],
            visible: this.can(PERMISSIONS.IPOS_READ),
          },
          {
            label: 'F.Statement Drafts',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/financial-statements'],
            visible: this.can(PERMISSIONS.DRAFTED_FINANCIAL_STATEMENTS_READ),
          },
        ],
      },
      {
        label: 'Control Panel',
        icon: 'pi pi-fw pi-sliders-h',
        items: [
          {
            label: 'Templates',
            icon: 'pi pi-fw pi-table',
            routerLink: ['/control-panel/templates'],
            visible: this.can(PERMISSIONS.TEMPLATES_READ),
          },
          {
            label: 'Global Stock Properties',
            icon: 'pi pi-fw pi-globe',
            routerLink: ['/control-panel/global-stock-properties'],
            visible: this.can(PERMISSIONS.GLOBAL_STOCK_PROPERTIES_READ),
          },
          {
            label: 'Min App Version',
            icon: 'pi pi-fw pi-mobile',
            routerLink: ['/control-panel/min-app-version'],
            visible: this.can(PERMISSIONS.SYSTEM_SETTINGS_READ),
          },
        ],
      },
    ];
  }
}
